import { useEffect, useMemo, useState } from "react";
import { colors, fonts, glyphs } from "../../../../../Styles";
import { NewButton, GenericDeviceTable, ResetTableModal, SecondaryPopover, ResetTableClearQueueModal } from "../../../../../Components";
import { ButtonTypes, ListData_ValueTypes, LC_TABLE_IDS } from "../../../../../Definitions";
import { LC_STATION, LCStationArgs, LCStationResponse } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/lcStation";
import { useLazyQuery } from "@apollo/client";
import { useAicoConnection } from "../../../../../Components/ToolsComponents/_ToolsComponents/aicoConnectionContext";

const columnsData = [
	{ headerValue: "ID", accessor: "id", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "lead device id", accessor: "device_id", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "lead port", accessor: "port", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "config count", accessor: "config_count", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "configs", accessor: "configs", type: ListData_ValueTypes.Div, sort: false },
]

// NOTE(MTH): test data
// const data: LCStationResponse = {
// 	controller: {
// 		lcStation: [
// 			{
// 				station_id: 1,
// 				device_id: 1,
// 				port: 1,
// 				config_count: 1,
// 				configs: [
// 					{
// 						value_type_id: 1,
// 						value: 1,
// 						value_min: 1,
// 						value_max: 1
// 					},
// 				]
// 			}
// 		]
// 	}
// }
// const loading = false;

export default function StationDataTable() {
	const [isResetTableModalOpen, setIsResetTableModalOpen] = useState<boolean>(false);
	const [isResetTableClearQueueModalOpen, setIsResetTableClearQueueModalOpen] = useState<boolean>(false);

	const { state: connectionState } = useAicoConnection();

	const [fetchData, {data, loading, error }] = useLazyQuery<LCStationResponse, LCStationArgs>(LC_STATION, { fetchPolicy: "network-only" });

	useEffect(() => {
		if (connectionState.deviceConnection) {
			fetchData({variables: connectionState.deviceConnection});
		}
	}, [connectionState.deviceConnection]);

	const _labelStyle = {...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, ...fonts.fontWeight2, whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "94px", minHeight: "15px", overflow: "hidden"};
	const _valueStyle = { ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, minHeight: "15px" };

	const tableData = useMemo(() => {
		return data?.controller.lcStation?.map((e, i) => {
			let configCell: React.ReactNode | undefined = undefined;

			if (e.configs.length) {
				configCell = (
					<div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
						<SecondaryPopover
							content={<glyphs.Note fillColor={colors.greyDarkPrimary} />}
							popoverHeader={"Components"}
							popoverContent={<>
								<div style={{ display: "grid", gridTemplateColumns: "94px 2px 94px 2px 94px 2px 94px", gridTemplateRows: "auto", gap: "10px", width: "100%" }}>
									<div style={{ display: "flex", alignItems: "start", justifyContent: "end", ..._labelStyle }}> Value Type Id </div>
									<div style={{ width: "2px", height: "100%", backgroundColor: colors.greyDarkSecondary, opacity: "0.5" }} />
									<div style={{display: "flex", alignItems: "start", justifyContent: "start", height: "18px", ..._labelStyle}}>Value</div>
									<div style={{ width: "2px", height: "100%", backgroundColor: colors.greyDarkSecondary, opacity: "0.5" }} />
									<div style={{display: "flex", alignItems: "start", justifyContent: "start", height: "18px", ..._labelStyle}}>Value Min</div>
									<div style={{ width: "2px", height: "100%", backgroundColor: colors.greyDarkSecondary, opacity: "0.5" }} />
									<div style={{display: "flex", alignItems: "start", justifyContent: "start", height: "18px", ..._labelStyle}}>Value Max</div>
								</div>
								{e.configs.map((config, index) =>
									<div key={index} style={{ display: "grid", gridTemplateColumns: "94px 2px 94px 2px 94px 2px 94px", gridTemplateRows: "auto", gap: "10px", width: "100%" }}>
										<div style={{ display: "flex", alignItems: "start", justifyContent: "end", ..._valueStyle }}> {config.value_type_id} </div>
										<div style={{ width: "2px", height: "100%", backgroundColor: colors.greyDarkSecondary, opacity: "0.5" }} />
										<div style={{ display: "flex", alignItems: "start", justifyContent: "start", height: "18px", ..._valueStyle }}>{config.value}</div>
										<div style={{ width: "2px", height: "100%", backgroundColor: colors.greyDarkSecondary, opacity: "0.5" }} />
										<div style={{ display: "flex", alignItems: "start", justifyContent: "start", height: "18px", ..._valueStyle }}>{config.value_min}</div>
										<div style={{ width: "2px", height: "100%", backgroundColor: colors.greyDarkSecondary, opacity: "0.5" }} />
										<div style={{ display: "flex", alignItems: "start", justifyContent: "start", height: "18px", ..._valueStyle }}>{config.value_max}</div>
									</div>
								)}
								</>
							}
						/>
					</div>
				);
			}

			return {
				...e,
				id: e.station_id,
				configs: configCell,
			}
		}) ?? [];
	}, [data]);

	return (
		<>
			<GenericDeviceTable
				title="Stations"
				columnsData={columnsData}
				data={tableData}
				loading={loading}
				error={error}
				onFetch={() => connectionState.deviceConnection && fetchData({ variables: connectionState.deviceConnection })}
				tabButtons={[
					<NewButton
						key="Reset table"
						content="Reset Table"
						startEnhancer={glyphs.ArrowReset({})}
						onClick={() => {
							setIsResetTableModalOpen(true);
						}}
						buttonType={ButtonTypes.tableButton}
						disabled={!connectionState.deviceConnection}
					/>,
					<NewButton
						key="Reset & clear"
						content="Reset & Clear"
						startEnhancer={glyphs.ArrowReset({})}
						onClick={() => {
							setIsResetTableClearQueueModalOpen(true);
						}}
						buttonType={ButtonTypes.tableButton}
						disabled={!connectionState.deviceConnection}
					/>
				]}
			/>
			{connectionState.deviceConnection && isResetTableModalOpen &&
				<ResetTableModal
					closeModal={()=>{ setIsResetTableModalOpen(false) }}
					onSuccess={() => fetchData({ variables: connectionState.deviceConnection })}
					tableId={LC_TABLE_IDS.stationConfig}
				/>
			}
			{connectionState.deviceConnection && isResetTableClearQueueModalOpen &&
				<ResetTableClearQueueModal
					closeModal={()=>{ setIsResetTableClearQueueModalOpen(false) }}
					onSuccess={() => fetchData({ variables: connectionState.deviceConnection })}
					tableId={LC_TABLE_IDS.stationConfig}
				/>
			}
		</>
	)
}